import { useMemo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { useMerchantMap } from '@/hooks';

interface CustomerFilterProps {
  /** Distinct merchantIds present in the loaded service list. */
  merchantIds: string[];
  /** Selected merchantId, '' = all customers. */
  value: string;
  onChange: (merchantId: string) => void;
}

/**
 * Issue #314: durchsuchbarer Kunden-Filter ueber der Service-Liste.
 * Tippbar nach Merchant-Name ODER Kundennummer — beide Felder sind ueber den
 * Combobox-Such-Match auffindbar (Name = Label, Kundennummer = Description).
 * Grenzt die bereits geladene Liste clientseitig auf einen Kunden ein.
 */
export function CustomerFilter({ merchantIds, value, onChange }: CustomerFilterProps) {
  const { t } = useTranslation('services');
  const { t: tCommon } = useTranslation('common');
  const { resolveMerchantName, resolveCustomerId } = useMerchantMap();

  const options = useMemo<ComboboxOption[]>(() => {
    const customerIdLabel = (id: string) =>
      `${tCommon('merchantCell.customerIdPrefix')} ${id}`;

    return merchantIds
      .map((id) => {
        const name = resolveMerchantName(id);
        const customerId = resolveCustomerId(id);
        const hasName = name !== id;
        const label = hasName ? name : customerId ? customerIdLabel(customerId) : id;
        return {
          value: id,
          label,
          description: hasName && customerId ? customerIdLabel(customerId) : undefined,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [merchantIds, resolveMerchantName, resolveCustomerId, tCommon]);

  return (
    <div className="flex items-center gap-2">
      <Combobox
        options={options}
        value={value}
        onChange={onChange}
        placeholder={t('filter.customerPlaceholder')}
        searchPlaceholder={t('filter.customerSearch')}
        emptyText={t('filter.customerEmpty')}
        className="w-full sm:w-80"
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 coarse:min-h-11 coarse:min-w-11"
          onClick={() => onChange('')}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('filter.reset')}</span>
        </Button>
      )}
    </div>
  );
}
